/**
 * AI-Assisted Setup client service — uploads contract and invoice
 * documents for AI-powered field extraction.
 *
 * The upload sends raw file content as the request body (not multipart).
 * Content-Type is set to the file's MIME type and x-filename carries
 * the original filename.
 *
 * Requirements: 19.1, 19.2, 19.3, 19.4
 */

// ─── Types ─────────────────────────────────────────────────────────────

/** A single extracted field with its value and confidence score. */
export interface ExtractedField<T = string> {
  value: T;
  /** Confidence score from 0 to 1. */
  confidence: number;
}

/** Vendor match information from fuzzy matching. */
export interface VendorMatchInfo {
  matchFound: boolean;
  matchedVendorId?: number;
  matchedVendorName?: string;
  similarity?: number;
  isNewVendor: boolean;
}

/** Result of processing a contract upload for AI-assisted setup. */
export interface ContractSetupResult {
  extractedFields: {
    vendorName: ExtractedField | null;
    contractTitle: ExtractedField | null;
    effectiveDate: ExtractedField | null;
    expirationDate: ExtractedField | null;
    contractValue: ExtractedField<number> | null;
    paymentTerms: ExtractedField | null;
    autoRenewalTerms: ExtractedField | null;
  };
  normalizedVendorName: string | null;
  vendorMatch: VendorMatchInfo | null;
  egnyteFilePath: string | null;
  egnyteFileId: string | null;
  rawText: string;
  disclaimer: string;
}

/** Result of processing an invoice upload for AI-assisted setup. */
export interface InvoiceSetupResult {
  extractedFields: {
    vendorName: ExtractedField | null;
    invoiceNumber: ExtractedField | null;
    invoiceDate: ExtractedField | null;
    dueDate: ExtractedField | null;
    poNumber: ExtractedField | null;
    subtotal: ExtractedField<number> | null;
    tax: ExtractedField<number> | null;
    totalAmount: ExtractedField<number> | null;
    lineItems: Array<{
      description: ExtractedField | null;
      quantity: ExtractedField<number> | null;
      unitPrice: ExtractedField<number> | null;
      amount: ExtractedField<number> | null;
    }>;
  };
  normalizedVendorName: string | null;
  vendorMatch: VendorMatchInfo | null;
  rawText: string;
  disclaimer: string;
}

// ─── Supported File Types ──────────────────────────────────────────────

const SUPPORTED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/tiff',
  'image/png',
  'text/html',
]);

/**
 * Validates that a file's MIME type is supported for AI extraction.
 * Returns true if supported, false otherwise.
 */
export function isSupportedFileType(mimeType: string): boolean {
  return SUPPORTED_MIME_TYPES.has(mimeType);
}

/** Human-readable list of accepted file types for display. */
export const ACCEPTED_FILE_EXTENSIONS = '.pdf,.doc,.docx,.jpg,.jpeg,.tiff,.tif,.png,.html,.htm';
export const ACCEPTED_FILE_DESCRIPTION = 'PDF, Word, JPEG, TIFF, PNG, or HTML';

// ─── Upload Functions ──────────────────────────────────────────────────

/**
 * Uploads a contract document for AI-assisted field extraction.
 *
 * Sends the raw file content as the request body with the file's MIME
 * type as Content-Type and the original filename in the x-filename header.
 *
 * @param file - The File object to upload.
 * @returns Extracted contract fields with confidence scores and vendor match info.
 */
export async function uploadContractForExtraction(file: File): Promise<ContractSetupResult> {
  const buffer = await file.arrayBuffer();

  const response = await fetch('/api/ai-setup/contract', {
    method: 'POST',
    headers: {
      'Content-Type': file.type,
      'x-filename': file.name,
    },
    credentials: 'include',
    body: buffer,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const message = data?.error?.message ?? `Upload failed with status ${response.status}`;
    throw new Error(message);
  }

  return response.json();
}

/**
 * Uploads an invoice document for AI-assisted field extraction.
 *
 * Sends the raw file content as the request body with the file's MIME
 * type as Content-Type and the original filename in the x-filename header.
 *
 * @param file - The File object to upload.
 * @returns Extracted invoice fields with confidence scores and vendor match info.
 */
export async function uploadInvoiceForExtraction(file: File): Promise<InvoiceSetupResult> {
  const buffer = await file.arrayBuffer();

  const response = await fetch('/api/ai-setup/invoice', {
    method: 'POST',
    headers: {
      'Content-Type': file.type,
      'x-filename': file.name,
    },
    credentials: 'include',
    body: buffer,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const message = data?.error?.message ?? `Upload failed with status ${response.status}`;
    throw new Error(message);
  }

  return response.json();
}

export const aiSetupService = {
  uploadContractForExtraction,
  uploadInvoiceForExtraction,
  isSupportedFileType,
};
